import { type FormEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { MatchPublic } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { cn } from '@/lib/cn';
import { ScoreInput, type Score } from '@/components/ScoreInput';

interface Props {
  match: MatchPublic;
  onSaved: () => void;
}

export function AdminResultForm({ match, onSaved }: Props) {
  const isKnockout = match.stage !== 'group';
  const existing = match.result;
  const [ft, setFt] = useState<Score>(existing?.fullTime ?? { team1: 0, team2: 0 });
  const [etOn, setEtOn] = useState<boolean>(!!existing?.extraTime);
  const [et, setEt] = useState<Score>(() => {
    const savedFt = existing?.fullTime ?? { team1: 0, team2: 0 };
    const savedEt = existing?.extraTime ?? { team1: 0, team2: 0 };
    return {
      team1: Math.max(savedEt.team1, savedFt.team1),
      team2: Math.max(savedEt.team2, savedFt.team2),
    };
  });
  const [pensOn, setPensOn] = useState<boolean>(!!existing?.penalties);
  const [pens, setPens] = useState<Score>(existing?.penalties ?? { team1: 0, team2: 0 });
  const [error, setError] = useState<string | null>(null);

  const ftDraw = ft.team1 === ft.team2;
  const etDraw = et.team1 === et.team2;

  // Enforce cascading rules in the UI — the server re-validates too.
  const canToggleEt = isKnockout && ftDraw;
  const canTogglePens = isKnockout && etOn && etDraw;

  const save = useMutation({
    mutationFn: async () => {
      const body: { fullTime: Score; extraTime: Score | null; penalties: Score | null } = {
        fullTime: ft,
        extraTime: canToggleEt && etOn ? et : null,
        penalties: canTogglePens && pensOn ? pens : null,
      };
      return api.post<{ match: MatchPublic }>(`/api/admin/matches/${match.id}/result`, body);
    },
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pensOn && canTogglePens && pens.team1 === pens.team2) {
      setError('Penalties must have a winner — no ties allowed');
      return;
    }
    save.mutate();
  }

  const team1Label = match.team1?.name ?? 'Team 1';
  const team2Label = match.team2?.name ?? 'Team 2';

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <div className="flex justify-between text-xs text-[color:var(--color-fg-muted)]">
        <span>{team1Label}</span>
        <span>{team2Label}</span>
      </div>
      <ScoreInput
        label="Full time"
        value={ft}
        onChange={(next) => {
          setFt(next);
          setEt((prev) => ({
            team1: Math.max(prev.team1, next.team1),
            team2: Math.max(prev.team2, next.team2),
          }));
        }}
      />

      {isKnockout ? (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-[color:var(--color-border)] p-3">
          <label className={cn('flex items-center gap-2 text-sm', !canToggleEt && 'opacity-60')}>
            <input
              type="checkbox"
              checked={etOn}
              disabled={!canToggleEt}
              onChange={(e) => {
                const next = e.target.checked;
                setEtOn(next);
                if (!next) setPensOn(false);
              }}
            />
            Went to extra time
          </label>
          {etOn ? (
            <ScoreInput
              label="Extra time"
              value={et}
              onChange={(next) =>
                setEt({
                  team1: Math.max(next.team1, ft.team1),
                  team2: Math.max(next.team2, ft.team2),
                })
              }
            />
          ) : null}
        </div>
      ) : null}

      {isKnockout ? (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-[color:var(--color-border)] p-3">
          <label className={cn('flex items-center gap-2 text-sm', !canTogglePens && 'opacity-60')}>
            <input
              type="checkbox"
              checked={pensOn}
              disabled={!canTogglePens}
              onChange={(e) => setPensOn(e.target.checked)}
            />
            Went to penalties
          </label>
          {pensOn ? (
            <ScoreInput label="Penalties" value={pens} onChange={setPens} />
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
      ) : null}

      <button
        type="submit"
        disabled={save.isPending}
        className="inline-flex h-11 items-center justify-center rounded-xl bg-[color:var(--color-accent)] font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60"
      >
        {save.isPending ? 'Saving…' : existing ? 'Update result' : 'Record result'}
      </button>
    </form>
  );
}
